import { useScrollBlock } from '@repo-level/core-front';
import { scrollToElement } from 'helpers/scroll';
import { useComponentVisible } from 'hooks/visibility';
import type React from 'react';
import type { RefObject } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import { ScModal } from './styled';

export interface ModalDefaultProps {
  onScrollTo?: <T extends HTMLElement>(ref: React.RefObject<T>) => void;
  onClose?: () => void;
}

export interface ModalProps extends ModalDefaultProps {
  setScrolledModalContainer?: (arg: Element) => void;
  isPortal?: boolean;
  children:
    | ((props: {
        ref: React.MutableRefObject<HTMLDivElement>;
        onScrollTo?: <T extends HTMLElement>(ref: React.RefObject<T>) => void;
        onClose?: () => void;
      }) => React.ReactNode)
    | React.ReactNode;
}

export function Modal({
  children,
  onClose,
  setScrolledModalContainer,
  isPortal,
}: ModalProps) {
  const refContainer = useRef<HTMLDivElement>(null);
  const rootElement = document.getElementById('root') as HTMLElement;

  const { ref } = useComponentVisible(true);
  const scrolledContainerRef = useRef<any>(null);

  useScrollBlock();

  const onScrollTo = <T extends HTMLElement>(ref: RefObject<T>) => {
    scrollToElement(ref, refContainer);
  };

  useEffect(() => {
    if (setScrolledModalContainer && scrolledContainerRef.current) {
      setScrolledModalContainer(scrolledContainerRef.current);
    }
  }, [setScrolledModalContainer]);

  const modalComponent = useMemo(
    () => (
      <ScModal ref={scrolledContainerRef}>
        <div className="bg" />
        <div className="container" ref={refContainer}>
          {typeof children === 'function'
            ? children({ ref, onScrollTo, onClose })
            : children}
        </div>
      </ScModal>
    ),
    [children, onClose, ref]
  );

  return (
    <>{isPortal ? createPortal(modalComponent, rootElement) : modalComponent}</>
  );
}
